import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";
import { Capacitor } from "@capacitor/core";
import { App as CapacitorApp } from "@capacitor/app";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

export type BackendStatus = "online" | "offline" | "checking" | "unknown";

interface BackendHealthContextValue {
  status: BackendStatus;
  lastChecked: Date | null;
  lastError: string | null;
  checkNow: () => Promise<void>;
  isHealthy: boolean;
  /** True if edge functions are confirmed working (Phase 2) */
  edgeFunctionsAvailable: boolean;
}

const BackendHealthContext = createContext<BackendHealthContextValue | undefined>(undefined);

// Battery-friendly: poll every 5 minutes (was 60s). Native pauses entirely in background.
const POLL_INTERVAL_MS = 5 * 60 * 1000; // 5 minutes
const HYSTERESIS_COUNT = 2; // Require 2 consecutive successes/failures before state change
const TOAST_COOLDOWN_MS = 30 * 60 * 1000; // 30 minutes cooldown between toasts
const TOAST_COOLDOWN_KEY = "backend_restored_toast_ts";

export function BackendHealthProvider({ children }: { children: React.ReactNode }) {
  const [status, setStatus] = useState<BackendStatus>("unknown");
  const [lastChecked, setLastChecked] = useState<Date | null>(null);
  const [lastError, setLastError] = useState<string | null>(null);
  const [edgeFunctionsAvailable, setEdgeFunctionsAvailable] = useState(false);
  const pollRef = useRef<NodeJS.Timeout | null>(null);
  
  // Hysteresis tracking - require consecutive successes/failures
  const consecutiveSuccessesRef = useRef(0);
  const consecutiveFailuresRef = useRef(0);
  
  // Stable state tracking (only changes after hysteresis threshold)
  const stableStatusRef = useRef<"up" | "down" | "unknown">("unknown");
  const hasEverBeenDownRef = useRef(false);
  
  // Recovery flush tracking
  const hasTriggeredRecoveryFlushRef = useRef(false);

  // Check if we're within toast cooldown
  const isWithinToastCooldown = useCallback((): boolean => {
    try {
      const lastToastTs = localStorage.getItem(TOAST_COOLDOWN_KEY);
      if (!lastToastTs) return false;
      const elapsed = Date.now() - parseInt(lastToastTs, 10);
      return elapsed < TOAST_COOLDOWN_MS;
    } catch {
      return false;
    }
  }, []);

  // Record toast shown
  const recordToastShown = useCallback(() => {
    try {
      localStorage.setItem(TOAST_COOLDOWN_KEY, Date.now().toString());
    } catch {
      // Ignore localStorage errors
    }
  }, []);

  // Automatic recovery is server-owned. No browser (including SA) may drain on health checks.
  const triggerRecoveryFlush = useCallback(async () => {
    if (hasTriggeredRecoveryFlushRef.current || isWithinToastCooldown()) return;
    hasTriggeredRecoveryFlushRef.current = true;
    recordToastShown();
    toast.info('Backend restored — queued tasks resume automatically', { id: 'backend-recovery' });
    setTimeout(() => { hasTriggeredRecoveryFlushRef.current = false; }, 10 * 60 * 1000);
  }, [isWithinToastCooldown, recordToastShown]);

  const checkHealth = useCallback(async () => {
    setStatus("checking");
    
    let checkSucceeded = false;
    let edgeFunctionsWorking = false;
    
    try {
      // Phase 1: Check if Supabase DB is reachable (lightweight query)
      // Use company_settings which all authenticated users can read via RLS
      const { error: dbError } = await supabase
        .from("company_settings")
        .select("id")
        .limit(1);

      if (dbError) {
        throw new Error(`Database unreachable: ${dbError.message}`);
      }

      // DB is reachable
      setStatus("online");
      setLastError(null);

      // Phase 2 check: Try the health edge function
      try {
        const { data, error } = await supabase.functions.invoke("health", {
          body: {},
        });

        if (!error && data?.ok) {
          edgeFunctionsWorking = true;
          checkSucceeded = true;
        }
      } catch {
        // Edge functions not deployed - that's fine for Phase 1
      }
      
      // Even without edge functions, DB being up counts as success
      if (!edgeFunctionsWorking) {
        checkSucceeded = true; // DB is up, that's enough
      }
    } catch (err: any) {
      console.warn("[BackendHealth] Health check failed:", err.message);
      setStatus("offline");
      setLastError(err.message || "Unknown error");
      checkSucceeded = false;
      edgeFunctionsWorking = false;
    } finally {
      setLastChecked(new Date());
    }
    
    // Update edge functions state
    setEdgeFunctionsAvailable(edgeFunctionsWorking);
    
    // Apply hysteresis for state transitions
    if (checkSucceeded) {
      consecutiveSuccessesRef.current++;
      consecutiveFailuresRef.current = 0;
      
      // Only transition to UP after HYSTERESIS_COUNT consecutive successes
      if (consecutiveSuccessesRef.current >= HYSTERESIS_COUNT) {
        const previousStableStatus = stableStatusRef.current;
        stableStatusRef.current = "up";
        
        // Only trigger recovery if we were previously confirmed DOWN (not just unknown/startup)
        if (previousStableStatus === "down" && hasEverBeenDownRef.current) {
          console.log("[BackendHealth] DOWN → UP transition detected");
          await triggerRecoveryFlush();
        }
      }
    } else {
      consecutiveFailuresRef.current++;
      consecutiveSuccessesRef.current = 0;
      
      // Only transition to DOWN after HYSTERESIS_COUNT consecutive failures
      if (consecutiveFailuresRef.current >= HYSTERESIS_COUNT) {
        stableStatusRef.current = "down";
        hasEverBeenDownRef.current = true; // Mark that we've been down at least once
      }
    }
  }, [triggerRecoveryFlush]);

  // Initial check on mount
  useEffect(() => {
    checkHealth();
  }, [checkHealth]);

  // Polling — pauses when app is backgrounded (native) or tab hidden (web) to save battery
  useEffect(() => {
    const startPolling = () => {
      if (pollRef.current) return;
      pollRef.current = setInterval(checkHealth, POLL_INTERVAL_MS);
    };
    const stopPolling = () => {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    };

    // Start initially
    startPolling();

    // Native: listen to Capacitor app state changes
    let nativeHandle: { remove: () => void } | null = null;
    if (Capacitor.isNativePlatform()) {
      CapacitorApp.addListener("appStateChange", ({ isActive }) => {
        if (isActive) {
          checkHealth(); // immediate check on resume
          startPolling();
        } else {
          stopPolling();
        }
      }).then((h) => { nativeHandle = h; });
    }

    // Web: pause when tab hidden
    const handleVisibility = () => {
      if (document.hidden) {
        stopPolling();
      } else {
        checkHealth();
        startPolling();
      }
    };
    document.addEventListener("visibilitychange", handleVisibility);

    return () => {
      stopPolling();
      nativeHandle?.remove();
      document.removeEventListener("visibilitychange", handleVisibility);
    };
  }, [checkHealth]);

  // Also check when window regains focus (user returns to tab) — web only
  useEffect(() => {
    if (Capacitor.isNativePlatform()) return; // handled by appStateChange
    const handleFocus = () => {
      checkHealth();
    };

    window.addEventListener("focus", handleFocus);
    return () => window.removeEventListener("focus", handleFocus);
  }, [checkHealth]);

  const value: BackendHealthContextValue = {
    status,
    lastChecked,
    lastError,
    checkNow: checkHealth,
    isHealthy: status === "online",
    edgeFunctionsAvailable,
  };

  return (
    <BackendHealthContext.Provider value={value}>
      {children}
    </BackendHealthContext.Provider>
  );
}

export function useBackendHealth() {
  const context = useContext(BackendHealthContext);
  if (!context) {
    throw new Error("useBackendHealth must be used within BackendHealthProvider");
  }
  return context;
}
